import { createPortal } from "react-dom";
import { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  FullscreenModal,
  Button,
  CalendarInput,
  DropFileInput,
  Toast,
} from "shared";
import Input from "shared/Input/Input";
import {
  useCloseAsset,
  useCloseInvestmentPolicy,
  useCloseTreasuryBonds,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import { CloseInvestmentModalConfig } from "./closeInvestmentConfigs";
import { TOAST_TYPES_ENUM } from "enums";
import "./CloseInvestmentModal.scss";

interface CloseInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (uuid: string) => void;
  item: any | null;
  config: CloseInvestmentModalConfig;
}

interface FormValues {
  salePrice: string;
  closedAt: string;
  contractFile: File[] | null;
  weight: string;
}

const CloseInvestmentModal = ({
  isOpen,
  onClose,
  onSuccess,
  item,
  config,
}: CloseInvestmentModalProps) => {
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const { mutateAsync: closeAsset, isPending: isPendingAsset } =
    useCloseAsset();
  const { mutateAsync: closePolicy, isPending: isPendingPolicy } =
    useCloseInvestmentPolicy();
  const { mutateAsync: closeBonds, isPending: isPendingBonds } =
    useCloseTreasuryBonds();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const {
    hookType,
    showSalePrice = false,
    closedAtLabel = "Data zamknięcia",
    closedAtRequired = true,
    showWeight = false,
  } = config;

  const isPending =
    (hookType === "asset"
      ? isPendingAsset
      : hookType === "policy"
      ? isPendingPolicy
      : isPendingBonds) || isUploadingFiles;

  const toastId =
    hookType === "policy"
      ? "close-investment-policy"
      : hookType === "bonds"
      ? "close-treasury-bonds"
      : "close-asset";

  const toastSuccessMessage =
    hookType === "policy"
      ? "Polisa inwestycyjna została zamknięta."
      : hookType === "bonds"
      ? "Obligacje skarbowe zostały zamknięte."
      : "Aktywo zostało zamknięte.";

  const validationSchema = Yup.object({
    salePrice: showSalePrice
      ? Yup.number()
          .required("Cena sprzedaży jest wymagana")
          .positive("Cena musi być dodatnia")
      : Yup.number().notRequired(),
    closedAt: closedAtRequired
      ? Yup.string().required(
          showSalePrice
            ? "Data sprzedaży jest wymagana"
            : "Data zamknięcia jest wymagana",
        )
      : Yup.string().notRequired(),
    contractFile: Yup.mixed().nullable(),
    weight: Yup.string().notRequired(),
  });

  const initialValues: FormValues = {
    salePrice: "",
    closedAt: "",
    contractFile: [],
    weight: item?.details?.weight != null ? String(item.details.weight) : "",
  };

  const uploadContractFiles = async (files: File[], investmentUuid: string) => {
    const userId: string | number =
      (me as any)?.user?.id ?? (me as any)?.id ?? "";

    await Promise.allSettled(
      files.slice(0, 10).map(async (contract: File) => {
        try {
          const uploaded = await uploadFile({
            file: contract,
            type: "attachment",
            parentId: userId,
          });

          const fileUuid = Array.isArray(uploaded)
            ? uploaded[0]?.uuid
            : (uploaded as any)?.uuid;

          if (fileUuid) {
            await attachFile({
              investmentUuid,
              fileUuid,
              role: "closure_contract",
            });
          }
        } catch {
          // Plik nie blokuje zamknięcia aktywa
        }
      }),
    );
  };

  const handleSuccess = async (values: FormValues) => {
    if (
      Array.isArray(values.contractFile) &&
      values.contractFile.length > 0 &&
      item?.uuid
    ) {
      const files = values.contractFile.filter((f) => f instanceof File);
      try {
        setIsUploadingFiles(true);
        await uploadContractFiles(files, item.uuid);
      } catch {
        // Pliki nie blokują zamknięcia aktywa
      } finally {
        setIsUploadingFiles(false);
      }
    }
    Toast({
      message: toastSuccessMessage,
      type: TOAST_TYPES_ENUM.SUCCESS,
      id: toastId,
    });
    onClose();
    if (item?.uuid) onSuccess?.(item.uuid);
  };

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      const basePayload = { uuid: item.uuid, closedAt: values.closedAt };

      if (hookType === "policy") {
        await closePolicy(basePayload, {
          onSuccess: () => handleSuccess(values),
        });
      } else if (hookType === "bonds") {
        await closeBonds(basePayload, {
          onSuccess: () => handleSuccess(values),
        });
      } else {
        await closeAsset(
          {
            ...basePayload,
            ...(showSalePrice && {
              salePrice: Number(values.salePrice),
              currencyUuid: item.currency?.uuid,
            }),
          },
          { onSuccess: () => handleSuccess(values) },
        );
      }
    } catch {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  const title =
    typeof config.title === "function" ? config.title(item) : config.title;
  const currencySymbol = item.currency?.currency || "";

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-investment-modal">
        <h2 className="fullscreen-modal__header">{title}</h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-investment-modal__form">
              <div className="close-investment-modal__fields">
                {showWeight && (
                  <div
                    className="close-investment-modal__field"
                    style={{ opacity: 0.5 }}
                  >
                    <Input
                      type="number"
                      name="weight"
                      label="Masa sprzedanego Kamienia szlachetnego"
                      precision={0}
                      data-suffix="g"
                      disabled
                    />
                  </div>
                )}
                {showSalePrice && (
                  <div className="close-investment-modal__field">
                    <Input
                      type="number"
                      name="salePrice"
                      label="Cena sprzedaży"
                      precision={2}
                      data-suffix={currencySymbol}
                      error={
                        (formik.touched.salePrice || formik.submitCount > 0) &&
                        formik.errors.salePrice
                          ? String(formik.errors.salePrice)
                          : undefined
                      }
                    />
                  </div>
                )}
                {closedAtRequired && (
                  <div className="close-investment-modal__field">
                    <Field
                      type="date"
                      name="closedAt"
                      label={closedAtLabel}
                      component={CalendarInput}
                      errors={formik.errors}
                      yearMonthPicker={true}
                    />
                  </div>
                )}
                <div className="close-investment-modal__field">
                  <Field
                    label="Umowy (opcjonalnie)"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                    multiple={10}
                    description="Maksymalnie 10 plików"
                  />
                </div>
              </div>
              <div className="close-investment-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseInvestmentModal;
